// Details the AI wasn't given are written as {{a few words saying what goes there}}.
// They're highlighted on the page for the user to fill in, and shown as lines to
// write on in downloaded files.

const BLANK = /\{\{\s*([^{}\n]{1,120}?)\s*\}\}/g;

// The same detail written slightly differently ("Sitter’s full name" and "sitter's
// full name.") is one blank: capitals, apostrophes and punctuation don't count
export function blankKey(label = "") {
  return label
    .toLowerCase()
    .replace(/['’‘`´]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

export const sameBlank = (a, b) => blankKey(a) === blankKey(b);

// The different blanks in the text, in the order they first appear, each named the
// way it was first written
export function listBlanks(text = "") {
  const seen = new Map();
  for (const match of text.matchAll(BLANK)) {
    const key = blankKey(match[1]);
    if (!seen.has(key)) seen.set(key, match[1]);
  }
  return [...seen.values()];
}

// Splits text into runs of { text } and { blank }
export function splitBlanks(text = "") {
  const runs = [];
  let last = 0;
  for (const match of text.matchAll(BLANK)) {
    if (match.index > last) {
      runs.push({ text: text.slice(last, match.index) });
    }
    runs.push({ blank: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    runs.push({ text: text.slice(last) });
  }
  return runs;
}

// The user's answers by blank, so any way a blank is written finds its answer
export function answersByKey(values = {}) {
  const answers = new Map();
  for (const [blank, value] of Object.entries(values)) {
    const answer = String(value ?? "").trim();
    if (answer) answers.set(blankKey(blank), answer);
  }
  return answers;
}

// Puts the user's answers in place of every copy of their blanks; blanks left empty
// stay
export function fillBlanks(text = "", values = {}) {
  const answers = answersByKey(values);
  if (!answers.size) return text;
  return text.replace(
    BLANK,
    (match, blank) => answers.get(blankKey(blank)) ?? match
  );
}

// For files people print or type into: a line to write on
export function blanksAsLines(text = "") {
  return text.replace(BLANK, "__________");
}

// For copied text: the description in square brackets
export function blanksAsLabels(text = "") {
  return text.replace(BLANK, (match, blank) => `[${blank}]`);
}
